import React, { useState, useEffect } from 'react';
import './CalendarEventModal.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

function CalendarEventModal({ event, onClose, onSave, onDelete }) {
  const [formData, setFormData] = useState({
    subject: '',
    start: '',
    end: '',
    location: '',
    body: '',
    isAllDay: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (event) {
      // Hvis vi redigerer eksisterende hendelse
      setFormData({
        subject: event.subject || '',
        start: formatDateTimeForInput(event.start),
        end: formatDateTimeForInput(event.end),
        location: event.location || '',
        body: event.body || '',
        isAllDay: event.isAllDay || false
      });
    } else {
      // Ny hendelse - sett default start-tid til nå
      const now = new Date();
      const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);

      setFormData({
        subject: '',
        start: formatDateTimeForInput(now),
        end: formatDateTimeForInput(oneHourLater),
        location: '',
        body: '',
        isAllDay: false
      });
    }
  }, [event]);

  // Konverter ISO dato til input datetime-local format
  const formatDateTimeForInput = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    // datetime-local input krever format: YYYY-MM-DDTHH:mm
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');

      // Konverter datetime-local til ISO format
      const eventData = {
        subject: formData.subject,
        start: new Date(formData.start).toISOString(),
        end: new Date(formData.end).toISOString(),
        location: formData.location,
        body: formData.body,
        isAllDay: formData.isAllDay
      };

      let response;

      if (event && event.id) {
        // Oppdater eksisterende hendelse
        response = await fetch(`${API_URL}/calendar/events/${event.id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(eventData)
        });
      } else {
        // Opprett ny hendelse
        response = await fetch(`${API_URL}/calendar/events`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(eventData)
        });
      }

      const data = await response.json();

      if (data.success) {
        onSave(data.event);
        onClose();
      } else {
        setError(data.error || 'Kunne ikke lagre hendelse');
      }
    } catch (err) {
      console.error('Feil ved lagring av kalenderhendelse:', err);
      setError('Nettverksfeil ved lagring');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!event || !event.id) return;

    if (!window.confirm('Er du sikker på at du vil slette denne kalenderhendelsen?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API_URL}/calendar/events/${event.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        onDelete(event.id);
        onClose();
      } else {
        setError(data.error || 'Kunne ikke slette hendelse');
      }
    } catch (err) {
      console.error('Feil ved sletting av kalenderhendelse:', err);
      setError('Nettverksfeil ved sletting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{event ? 'Rediger kalenderhendelse' : 'Ny kalenderhendelse'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="subject">Tittel *</label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              required
              placeholder="F.eks. Møte med klient"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="start">Start *</label>
              <input
                type="datetime-local"
                id="start"
                name="start"
                value={formData.start}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="end">Slutt *</label>
              <input
                type="datetime-local"
                id="end"
                name="end"
                value={formData.end}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="location">Sted</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="F.eks. Kontoret, Teams, osv."
            />
          </div>

          <div className="form-group">
            <label htmlFor="body">Beskrivelse</label>
            <textarea
              id="body"
              name="body"
              value={formData.body}
              onChange={handleChange}
              rows="4"
              placeholder="Ekstra informasjon om møtet..."
            />
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="isAllDay"
                checked={formData.isAllDay}
                onChange={handleChange}
              />
              <span>Heldagshendelse</span>
            </label>
          </div>

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          <div className="modal-actions">
            {event && event.id && (
              <button
                type="button"
                className="btn-delete"
                onClick={handleDelete}
                disabled={loading}
              >
                🗑️ Slett
              </button>
            )}

            <div className="modal-actions-right">
              <button
                type="button"
                className="btn-cancel"
                onClick={onClose}
                disabled={loading}
              >
                Avbryt
              </button>

              <button
                type="submit"
                className="btn-save"
                disabled={loading}
              >
                {loading ? 'Lagrer...' : (event ? 'Oppdater' : 'Opprett')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CalendarEventModal;
